import { useState, useCallback } from 'react';
import dashboardService from '../services/dashboardService';

/**
 * Custom hook for managing dashboard data and operations
 * @returns {Object} Dashboard state and methods
 */
export const useDashboard = () => {
    // State management for different sections
    // Using a more generic state approach or individual states? 
    // Given the variety of independent widgets, specific fetchers are better.

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    // Helper wrapper for api calls to manage loading/error state
    const wrapApiCall = useCallback(async (apiFunc, ...args) => {
        setLoading(true);
        setError(null);
        try {
            const result = await apiFunc(...args);
            if (result.success) {
                return { success: true, data: result.data };
            } else {
                setError(result.error);
                return { success: false, error: result.error };
            }
        } catch (err) {
            const errorMsg = 'An unexpected error occurred';
            setError(errorMsg);
            return { success: false, error: errorMsg };
        } finally {
            setLoading(false);
        }
    }, []);

    // --- Main Dashboard ---
    const getDashboardData = useCallback((params) => wrapApiCall(dashboardService.getDashboardData.bind(dashboardService), params), [wrapApiCall]);

    // --- Sales ---
    const getSalesTopProducts = useCallback((params) => wrapApiCall(dashboardService.getSalesTopProducts.bind(dashboardService), params), [wrapApiCall]);
    const getSalesOverview = useCallback((params) => wrapApiCall(dashboardService.getSalesOverview.bind(dashboardService), params), [wrapApiCall]);
    const getSalesTrend = useCallback((params) => wrapApiCall(dashboardService.getSalesTrend.bind(dashboardService), params), [wrapApiCall]);
    const getSalesByCategory = useCallback((params) => wrapApiCall(dashboardService.getSalesByCategory.bind(dashboardService), params), [wrapApiCall]);
    const getSalesByWarehouse = useCallback((params) => wrapApiCall(dashboardService.getSalesByWarehouse.bind(dashboardService), params), [wrapApiCall]);
    const getSalesOrderStatus = useCallback((params) => wrapApiCall(dashboardService.getSalesOrderStatus.bind(dashboardService), params), [wrapApiCall]);

    // --- Delivery ---
    const getDeliveryChallans = useCallback((params) => wrapApiCall(dashboardService.getDeliveryChallans.bind(dashboardService), params), [wrapApiCall]);
    const getDeliveryPerformance = useCallback((params) => wrapApiCall(dashboardService.getDeliveryPerformance.bind(dashboardService), params), [wrapApiCall]);
    const getDeliveryPersonnel = useCallback((params) => wrapApiCall(dashboardService.getDeliveryPersonnel.bind(dashboardService), params), [wrapApiCall]);

    // --- Purchase ---
    const getPurchaseOverview = useCallback((params) => wrapApiCall(dashboardService.getPurchaseOverview.bind(dashboardService), params), [wrapApiCall]);
    const getPurchaseTopSuppliers = useCallback((params) => wrapApiCall(dashboardService.getPurchaseTopSuppliers.bind(dashboardService), params), [wrapApiCall]);
    const getPurchaseTrend = useCallback((params) => wrapApiCall(dashboardService.getPurchaseTrend.bind(dashboardService), params), [wrapApiCall]);

    // --- Inventory ---
    const getInventoryWarehouseStock = useCallback((params) => wrapApiCall(dashboardService.getInventoryWarehouseStock.bind(dashboardService), params), [wrapApiCall]);
    const getInventoryLowStock = useCallback((params) => wrapApiCall(dashboardService.getInventoryLowStock.bind(dashboardService), params), [wrapApiCall]);

    // --- Customers ---
    const getCustomersTop = useCallback((params) => wrapApiCall(dashboardService.getCustomersTop.bind(dashboardService), params), [wrapApiCall]);
    const getCustomersGrowth = useCallback((params) => wrapApiCall(dashboardService.getCustomersGrowth.bind(dashboardService), params), [wrapApiCall]);

    // --- Inquiries ---
    const getInquiriesSummary = useCallback((params) => wrapApiCall(dashboardService.getInquiriesSummary.bind(dashboardService), params), [wrapApiCall]);
    const getInquiriesTrend = useCallback((params) => wrapApiCall(dashboardService.getInquiriesTrend.bind(dashboardService), params), [wrapApiCall]);

    // --- Financial ---
    const getFinancialGstSummary = useCallback((params) => wrapApiCall(dashboardService.getFinancialGstSummary.bind(dashboardService), params), [wrapApiCall]);
    const getFinancialTcs = useCallback((params) => wrapApiCall(dashboardService.getFinancialTcs.bind(dashboardService), params), [wrapApiCall]);
    const getFinancialRevenueComparison = useCallback((params) => wrapApiCall(dashboardService.getFinancialRevenueComparison.bind(dashboardService), params), [wrapApiCall]);


    return {
        // Global State
        loading,
        error,

        // Methods
        getDashboardData,

        // Sales
        getSalesTopProducts,
        getSalesOverview,
        getSalesTrend,
        getSalesByCategory,
        getSalesByWarehouse,
        getSalesOrderStatus,

        // Delivery
        getDeliveryChallans,
        getDeliveryPerformance,
        getDeliveryPersonnel,

        // Purchase
        getPurchaseOverview,
        getPurchaseTopSuppliers,
        getPurchaseTrend,

        // Inventory
        getInventoryWarehouseStock,
        getInventoryLowStock,

        // Customers
        getCustomersTop,
        getCustomersGrowth,

        // Inquiries
        getInquiriesSummary,
        getInquiriesTrend,

        // Financial
        getFinancialGstSummary,
        getFinancialTcs,
        getFinancialRevenueComparison
    };
};
